import { QueryClientProvider } from '@tanstack/react-query'
import { Check, ChevronDown } from 'lucide-react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { parseError } from '@/lib/errors'
import { cn } from '@/lib/utils'
import { useAuthStatus } from '@/models/auth'
import { insureQueryClient } from '@/models/insureQueryClient'
import { orgMembershipLabel, useCurrentUser, useSwitchOrg } from '@/models/user'

// ph-no-capture: replay masks input values, not rendered text, and this is a customer's name.
const NAME_CLASS = 'ph-no-capture truncate text-lg font-medium text-ink-900 capitalize'

/**
 * The customer's name in the top bar, and the way to another org for users in more than one.
 * The active org lives on the user record (`current_org`), never in the URL, so the switch is a
 * single request and every subsequent query is scoped server-side.
 */
export function OrgSwitcher() {
  const { user } = useCurrentUser({ enabled: false })
  const navigate = useNavigate()
  const switchOrg = useSwitchOrg()

  if (!user?.currentOrg) return null

  const memberships = user.organisations ?? []
  const currentOrgId = user.currentOrg.id
  // Lowercased so CSS capitalize evens out carrier names registered in all caps.
  const currentName = (user.currentOrg.business?.name || user.currentOrg.name || '').toLowerCase()

  if (memberships.length < 2) {
    return <span className={cn(NAME_CLASS, 'px-2')}>{currentName}</span>
  }

  function handleSelect(orgId: string) {
    if (orgId === currentOrgId) return
    switchOrg.mutate(orgId, {
      onSuccess: (updated) => {
        const name = updated.currentOrg.business?.name || updated.currentOrg.name
        toast.success(`Switched to ${name}`)
        void navigate('/insure')
      },
      onError: (error) => toast.error(parseError(error)),
    })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        disabled={switchOrg.isPending}
        className="hover:bg-ink-50 focus-visible:ring-brand-700/50 flex min-w-0 cursor-pointer items-center gap-1 rounded-md px-2 py-1 outline-none focus-visible:ring-[3px]"
      >
        <span className={NAME_CLASS}>{currentName}</span>
        <ChevronDown className="text-ink-300 size-3 shrink-0" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Switch organization</DropdownMenuLabel>
        {memberships.map((membership) => (
          <DropdownMenuItem
            key={membership.orgId}
            onSelect={() => handleSelect(membership.orgId)}
            className="justify-between gap-2"
          >
            <span className="truncate">{orgMembershipLabel(membership)}</span>
            <Check className={cn('size-4 shrink-0', membership.orgId !== currentOrgId && 'invisible')} />
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** The customer's name in the shared shell's bar on Cai and Fuel, for any account with an Insure user; else the fallback. */
export function ShellOrgSwitcher({ fallback }: { fallback?: ReactNode }) {
  return (
    <QueryClientProvider client={insureQueryClient}>
      <OrgSwitcherOr fallback={fallback} />
    </QueryClientProvider>
  )
}

function OrgSwitcherOr({ fallback }: { fallback?: ReactNode }) {
  const { data: authStatus } = useAuthStatus()
  const { user } = useCurrentUser({ enabled: !!authStatus?.authenticated })
  return user?.currentOrg ? <OrgSwitcher /> : fallback
}
